import React, { useState } from 'react';
import { nasAPI } from '../../services/api';

/**
 * "Add Ruijie Device" — Ruijie Cloud API setup wizard.
 *
 * The Authenticate / Network Group / User Group steps call real backend
 * endpoints (/api/nas/ruijie/*), backed by ruijieCloud.service.js — wired up
 * to Ruijie's real Cloud Open API and live-verified against a real account
 * (see that file's header for the confirmed endpoint shapes). The
 * notConfigured error path below still applies if baseUrl is missing/wrong,
 * but no longer fires unconditionally.
 *
 * Registration only — there is no config-push for Ruijie Cloud devices
 * (Ruijie's public API has no documented endpoint for it); RADIUS/portal
 * setup for a cloud-mode device has to be entered by hand in Ruijie Cloud's
 * own console. vendors/ruijie.service.js's pushConfig returns those exact
 * values to copy in rather than attempting a push.
 */

const STEPS = [
    { key: 'credentials',  label: 'API Credentials',  desc: 'Enter Cloud API credentials' },
    { key: 'authenticate', label: 'Authentication',   desc: 'Verify credentials and generate tokens' },
    { key: 'network',      label: 'Network Group',    desc: 'Select network group for configuration' },
    { key: 'user',         label: 'User Group',       desc: 'Select user group for the selected network' },
    { key: 'login',        label: 'Login Method',     desc: 'Choose how guests will receive WiFi access' },
    { key: 'password',     label: 'Password Options', desc: 'Select password/voucher field mapping' },
    { key: 'complete',     label: 'Complete Setup',   desc: 'Save device configuration' },
];

const LOGIN_METHODS = [
    { value: 'voucher',        label: 'Voucher', desc: 'Guest enters a one-time voucher code' },
    { value: 'password',       label: 'Shared Password', desc: 'Guest enters a shared WiFi password' },
    { value: 'sms',            label: 'SMS OTP', desc: 'Guest verifies via SMS one-time code' },
    { value: 'click-through',  label: 'Click-through', desc: 'Guest just accepts terms, no credential' },
];

const RuijieCloudWizard = ({ value, onChange, onCancel, branchName, saving }) => {
    const [stepIndex, setStepIndex] = useState(0);
    const [authenticating, setAuthenticating] = useState(false);
    const [authError, setAuthError] = useState('');
    const [loadingNetworkGroups, setLoadingNetworkGroups] = useState(false);
    const [loadingUserGroups, setLoadingUserGroups] = useState(false);
    const [networkGroups, setNetworkGroups] = useState([]);
    const [userGroups, setUserGroups] = useState([]);
    const [groupsError, setGroupsError] = useState('');

    const cfg = value || {};
    const step = STEPS[stepIndex];

    const set = (patch) => onChange({ ...cfg, ...patch });

    const goTo = (idx) => setStepIndex(Math.max(0, Math.min(STEPS.length - 1, idx)));
    const next = () => goTo(stepIndex + 1);
    const back = () => goTo(stepIndex - 1);

    const fetchNetworkGroups = async (accessToken) => {
        setLoadingNetworkGroups(true);
        setGroupsError('');
        try {
            const res = await nasAPI.ruijieNetworkGroups(accessToken, cfg.baseUrl);
            setNetworkGroups(res.data.data || []);
        } catch (err) {
            setGroupsError(err.response?.data?.notConfigured
                ? "Ruijie Cloud API isn't connected yet — this backend needs real Ruijie API endpoint details before Network Groups can load."
                : (err.response?.data?.message || 'Failed to load network groups'));
        } finally {
            setLoadingNetworkGroups(false);
        }
    };

    const fetchUserGroups = async (networkGroupId) => {
        setLoadingUserGroups(true);
        setGroupsError('');
        try {
            const res = await nasAPI.ruijieUserGroups(cfg.accessToken, cfg.baseUrl, networkGroupId);
            setUserGroups(res.data.data || []);
        } catch (err) {
            setGroupsError(err.response?.data?.notConfigured
                ? "Ruijie Cloud API isn't connected yet — this backend needs real Ruijie API endpoint details before User Groups can load."
                : (err.response?.data?.message || 'Failed to load user groups'));
        } finally {
            setLoadingUserGroups(false);
        }
    };

    const handleAuthenticate = async () => {
        setAuthenticating(true);
        setAuthError('');
        try {
            const res = await nasAPI.ruijieAuthenticate({
                appId: cfg.appId,
                secretKey: cfg.secretKey,
                baseUrl: cfg.baseUrl,
            });
            const token = res.data.data.accessToken;
            set({ accessToken: token });
            next();
            fetchNetworkGroups(token);
        } catch (err) {
            setAuthError(err.response?.data?.notConfigured
                ? "Ruijie Cloud API isn't connected yet — this backend has no verified Ruijie Cloud endpoint details, so Authenticate can't complete. The rest of this wizard still works with manual selections once that's wired up."
                : (err.response?.data?.message || 'Authentication failed. Check your App ID and Secret Key.'));
        } finally {
            setAuthenticating(false);
        }
    };

    const selectNetworkGroup = (group) => {
        set({ networkGroup: { id: group.id, name: group.name, type: group.type } });
        setUserGroups([]);
        fetchUserGroups(group.id);
    };

    const selectUserGroup = (group) => {
        set({ userGroup: { id: group.id, name: group.name } });
    };

    return (
        <div className="ruijie-wizard" style={{ display: 'flex', gap: '1.25rem', border: '1px solid #fde68a', borderRadius: 10, overflow: 'hidden', marginTop: '1rem' }}>
            {/* Left — vertical stepper */}
            <div style={{ width: 220, flexShrink: 0, background: '#fffbeb', padding: '1rem 0.75rem', borderRight: '1px solid #fde68a' }}>
                {branchName && (
                    <div style={{ fontSize: '0.72rem', color: '#92400e', marginBottom: '0.75rem', fontWeight: 600 }}>
                        Branch: {branchName}
                    </div>
                )}
                {STEPS.map((s, i) => {
                    const done = i < stepIndex;
                    const current = i === stepIndex;
                    return (
                        <div key={s.key} onClick={() => i < stepIndex && goTo(i)}
                            style={{
                                display: 'flex', gap: 8, padding: '0.5rem 0.25rem',
                                cursor: i < stepIndex ? 'pointer' : 'default',
                                opacity: current || done ? 1 : 0.55,
                            }}>
                            <div style={{
                                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.7rem', fontWeight: 700,
                                background: done ? '#111827' : current ? '#1f3655' : '#e5e7eb',
                                color: done || current ? '#fff' : '#6b7280',
                            }}>
                                {done ? <i className="fas fa-check"></i> : i + 1}
                            </div>
                            <div>
                                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#111827' }}>{s.label}</div>
                                <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>{s.desc}</div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Right — step content */}
            <div style={{ flex: 1, padding: '1rem 1.25rem 1rem 0' }}>
                {step.key === 'credentials' && (
                    <div>
                        <h4 style={{ margin: '0 0 0.25rem' }}>Ruijie Cloud API Credentials</h4>
                        <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
                            Enter your Ruijie Cloud API credentials to connect to the system.
                        </p>
                        <div className="form-grid">
                            <div className="form-group-modern">
                                <label>Device Name *</label>
                                <input type="text" placeholder="Enter a unique name for this device"
                                    value={cfg.deviceName || ''} onChange={e => set({ deviceName: e.target.value })} />
                            </div>
                            <div className="form-group-modern">
                                <label>App ID *</label>
                                <input type="text" placeholder="Your Ruijie Cloud App ID"
                                    value={cfg.appId || ''} onChange={e => set({ appId: e.target.value })} />
                            </div>
                            <div className="form-group-modern">
                                <label>Secret Key *</label>
                                <input type="password" placeholder="Your Ruijie Cloud Secret Key"
                                    value={cfg.secretKey || ''} onChange={e => set({ secretKey: e.target.value })} />
                            </div>
                            <div className="form-group-modern">
                                <label>Base URL *</label>
                                <input type="text" style={{ fontFamily: 'monospace' }} placeholder="https://cloud-as.ruijienetworks.com"
                                    value={cfg.baseUrl || 'https://cloud-as.ruijienetworks.com'} onChange={e => set({ baseUrl: e.target.value })} />
                                <p className="input-hint">Ruijie Cloud API base URL</p>
                            </div>
                            <div className="form-group-modern full-width">
                                <label>Description</label>
                                <textarea placeholder="e.g., Main branch Ruijie Cloud device for guest WiFi management"
                                    value={cfg.description || ''} onChange={e => set({ description: e.target.value })} rows={2} />
                                <p className="input-hint">Optional description for this device</p>
                            </div>
                        </div>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.78rem', color: '#1e3a8a', marginTop: '0.5rem' }}>
                            <i className="fas fa-info-circle"></i>&nbsp; <strong>Need API credentials?</strong> Contact your Ruijie Cloud administrator or visit the Ruijie Cloud console to generate API credentials.
                        </div>
                    </div>
                )}

                {step.key === 'authenticate' && (
                    <div>
                        <h4 style={{ margin: '0 0 0.25rem' }}>Verify Credentials</h4>
                        <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
                            We'll verify your App ID and Secret Key with Ruijie Cloud and generate an access token.
                        </p>
                        {authError && (
                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.8rem', color: '#991b1b', marginBottom: '0.75rem' }}>
                                <i className="fas fa-exclamation-triangle"></i> {authError}
                            </div>
                        )}
                        <button type="button" className="btn-primary" onClick={handleAuthenticate} disabled={authenticating || !cfg.appId || !cfg.secretKey}>
                            {authenticating ? 'Authenticating…' : 'Authenticate'}
                        </button>
                    </div>
                )}

                {step.key === 'network' && (
                    <div>
                        <h4 style={{ margin: '0 0 0.25rem' }}>Network Group Selection</h4>
                        <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
                            Select the network group where you want to manage devices and generate vouchers.
                        </p>
                        {groupsError && (
                            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.8rem', color: '#92400e', marginBottom: '0.75rem' }}>
                                <i className="fas fa-exclamation-triangle"></i> {groupsError}
                            </div>
                        )}
                        <select className="input-modern" value={cfg.networkGroup?.id || ''}
                            onChange={e => {
                                const g = networkGroups.find(ng => ng.id === e.target.value);
                                if (g) selectNetworkGroup(g);
                            }}
                            disabled={loadingNetworkGroups || networkGroups.length === 0}>
                            <option value="">{loadingNetworkGroups ? 'Loading…' : 'Select a network group'}</option>
                            {networkGroups.map(g => (
                                <option key={g.id} value={g.id}>{g.name} — ID: {g.id} • Type: {g.type}</option>
                            ))}
                        </select>
                        {cfg.networkGroup?.id && (
                            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.8rem', marginTop: '0.75rem' }}>
                                <strong>Selected Network Group</strong>
                                <div>Name: {cfg.networkGroup.name}</div>
                                <div>Group ID: {cfg.networkGroup.id}</div>
                                <div>Type: {cfg.networkGroup.type}</div>
                            </div>
                        )}
                    </div>
                )}

                {step.key === 'user' && (
                    <div>
                        <h4 style={{ margin: '0 0 0.25rem' }}>User Group Selection</h4>
                        <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
                            Select the user group that will be used for voucher generation and user management.
                        </p>
                        {groupsError && (
                            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.8rem', color: '#92400e', marginBottom: '0.75rem' }}>
                                <i className="fas fa-exclamation-triangle"></i> {groupsError}
                            </div>
                        )}
                        {!loadingUserGroups && !groupsError && userGroups.length === 0 && (
                            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.8rem', color: '#92400e' }}>
                                <i className="fas fa-exclamation-triangle"></i> No user groups found for the selected network group. Please ensure the network group has user groups configured.
                            </div>
                        )}
                        {userGroups.length > 0 && (
                            <select className="input-modern" value={cfg.userGroup?.id || ''}
                                onChange={e => {
                                    const g = userGroups.find(ug => ug.id === e.target.value);
                                    if (g) selectUserGroup(g);
                                }}>
                                <option value="">Select a user group</option>
                                {userGroups.map(g => (
                                    <option key={g.id} value={g.id}>{g.name} — ID: {g.id}</option>
                                ))}
                            </select>
                        )}
                    </div>
                )}

                {step.key === 'login' && (
                    <div>
                        <h4 style={{ margin: '0 0 0.25rem' }}>Login Method</h4>
                        <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
                            Choose how guests will receive WiFi access on this network.
                        </p>
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                            {LOGIN_METHODS.map(m => (
                                <label key={m.value} style={{
                                    display: 'flex', gap: 10, alignItems: 'flex-start', padding: '0.6rem 0.8rem',
                                    border: `1px solid ${cfg.loginMethod === m.value ? '#1f3655' : '#e5e7eb'}`,
                                    borderRadius: 8, cursor: 'pointer',
                                    background: cfg.loginMethod === m.value ? '#f1f4f8' : '#fff',
                                }}>
                                    <input type="radio" name="ruijieLoginMethod" checked={cfg.loginMethod === m.value}
                                        onChange={() => set({ loginMethod: m.value })} style={{ marginTop: 3 }} />
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{m.label}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{m.desc}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {step.key === 'password' && (
                    <div>
                        <h4 style={{ margin: '0 0 0.25rem' }}>WiFi Password Policy</h4>
                        <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
                            Generated WiFi passwords/vouchers will be transformed using this case mode.
                        </p>
                        <div className="form-group-modern">
                            <label>Password Case</label>
                            <select className="input-modern" value={cfg.passwordOptions?.caseMode || 'preserve'}
                                onChange={e => set({ passwordOptions: { ...cfg.passwordOptions, caseMode: e.target.value } })}>
                                <option value="preserve">Preserve Original Case</option>
                                <option value="upper">UPPERCASE</option>
                                <option value="lower">lowercase</option>
                            </select>
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '0.5rem', fontSize: '0.85rem' }}>
                            <input type="checkbox" checked={!!cfg.passwordOptions?.hidePassword}
                                onChange={e => set({ passwordOptions: { ...cfg.passwordOptions, hidePassword: e.target.checked } })} />
                            Hide WiFi password in guest dialog
                        </label>
                    </div>
                )}

                {step.key === 'complete' && (
                    <div>
                        <h4 style={{ margin: '0 0 0.25rem' }}>Review &amp; Save</h4>
                        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.75rem 0.9rem', fontSize: '0.82rem', display: 'grid', gap: 4 }}>
                            <div><strong>Device Name:</strong> {cfg.deviceName || '—'}</div>
                            <div><strong>Base URL:</strong> {cfg.baseUrl}</div>
                            <div><strong>Network Group:</strong> {cfg.networkGroup?.name || '—'}</div>
                            <div><strong>User Group:</strong> {cfg.userGroup?.name || '—'}</div>
                            <div><strong>Login Method:</strong> {LOGIN_METHODS.find(m => m.value === cfg.loginMethod)?.label || 'Voucher'}</div>
                            <div><strong>Password Case:</strong> {cfg.passwordOptions?.caseMode || 'preserve'}</div>
                        </div>
                    </div>
                )}

                {/* Nav */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.25rem', paddingTop: '0.75rem', borderTop: '1px solid #f3f4f6' }}>
                    <button type="button" className="btn-text" onClick={onCancel}>
                        <i className="fas fa-times"></i> Cancel
                    </button>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {stepIndex > 0 && step.key !== 'authenticate' && (
                            <button type="button" className="btn-secondary" onClick={back}>Back</button>
                        )}
                        {step.key !== 'authenticate' && step.key !== 'complete' && (
                            <button type="button" className="btn-primary" onClick={next}
                                disabled={step.key === 'network' && !cfg.networkGroup?.id}>
                                Next
                            </button>
                        )}
                        {step.key === 'complete' && (
                            // Nested inside the parent <form onSubmit={handleSubmit}> in Routers.jsx —
                            // submitting here reuses the exact same create/update NAS logic.
                            <button type="submit" className="btn-primary" disabled={saving}>
                                {saving ? 'Saving…' : 'Save Device'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RuijieCloudWizard;
